import { formatDate, KeyValue } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { IChangedFieldDetail } from '../pay-categories/pay-categories.type';

/**
 * Handles display of entity changes (history and future changes)
 */
@Component({
	selector: 'app-changes-display',
	styleUrls: ['./changes-display.component.scss'],
	templateUrl: './changes-display.component.html'
})
export class ChangesDisplayComponent implements OnInit {
	@Input() public history: IChangedFieldDetail[] = [];
	@Input() public idPrefix: string;

	public displayHistory: Map<string, IChangedFieldDetailFormatted[]> = null;
	public newRecordText: string = 'New record went into effect.';

	public ngOnInit(): void {
		if (!history) {
			return;
		}

		this.cleanChangeDetailsForDisplay();
	}

	public reverseKeyOrder = (
		a: KeyValue<string, IChangedFieldDetailFormatted[]>,
		b: KeyValue<string, IChangedFieldDetailFormatted[]>
	): number => {
		const date1: Date = new Date(a.key);
		const date2: Date = new Date(b.key);

		return +date2 - +date1;
	};

	private cleanChangeDetailsForDisplay(): void {
		this.createEffectiveDateGroups();
		this.formatRecordsForDisplay();
		this.sortNewRecordEntries();
	}

	private sortNewRecordEntries(): void {
		for (const key of Array.from(this.displayHistory.keys())) {
			// Sorting occurs on the API for the most part. The only thing that needs to happen here
			// is to move any remaining NewRecordCreated field to the end of the array.
			const fieldDetails: IChangedFieldDetailFormatted[] = this.displayHistory.get(key);
			const newRecordIndex: number = fieldDetails.findIndex(f => f.field === this.newRecordText);
			if (newRecordIndex > -1) {
				fieldDetails.push(fieldDetails.splice(newRecordIndex, 1)[0]);
			}

			this.displayHistory.set(key, fieldDetails);
		}
	}

	private formatRecordsForDisplay(): void {
		for (const key of Array.from(this.displayHistory.keys())) {
			const fieldDetails: IChangedFieldDetailFormatted[] = this.displayHistory.get(key);
			for (const detail of fieldDetails) {
				if (detail.field === 'PayCategoryNoteArchived') {
					continue;
				} else if (detail.field === 'NewRecordCreated') {
					// remove all other fields for initial creation
					detail.field = this.newRecordText;
					detail.newValue = null;
					detail.oldValue = null;
					//this.displayHistory.set(key, [detail]);
				} else if (detail.field === 'WorkdayPayCode' && !detail.newValue) {
					detail.field = 'Workday Pay Code removed';
					detail.newValue = null;
					detail.oldValue = null;
				} else {
					detail.field = this.normalizeFieldDisplayName(detail, detail.fieldNameIsNormalized);
					detail.isColor = this.isFieldAColorField(detail.field);
					detail.isIcon = this.isFieldAnIconField(detail.field);
					if (detail.effectiveDate) {
						detail.isFuture = this.isThisAFutureChange(detail.effectiveDate);
					}
				}
			}
		}
	}

	private createEffectiveDateGroups(): void {
		const effectiveDatesMap: Map<string, IChangedFieldDetailFormatted[]> = new Map<
			string,
			IChangedFieldDetailFormatted[]
		>();

		_.orderBy(this.history, (p: IChangedFieldDetail) => {
			p.effectiveDate ? p.effectiveDate : p.changeDate;
		}).forEach((item: IChangedFieldDetail) => {
			let formattedEffDate: string;

			item.effectiveDate
				? (formattedEffDate = formatDate(item.effectiveDate, 'MMMM-yyyy', 'en-US'))
				: (formattedEffDate = formatDate(item.changeDate, 'MMMM-yyyy', 'en-US'));

			effectiveDatesMap.has(formattedEffDate)
				? effectiveDatesMap.get(formattedEffDate).push(<IChangedFieldDetailFormatted>item)
				: effectiveDatesMap.set(formattedEffDate, [<IChangedFieldDetailFormatted>item]);
		});

		this.displayHistory = effectiveDatesMap;
	}

	// tslint:disable-next-line: cyclomatic-complexity
	private normalizeFieldDisplayName(
		detail: IChangedFieldDetailFormatted,
		alreadyNormalized: boolean = false
	): string {
		if (alreadyNormalized) {
			return detail.field;
		}
		switch (detail.field) {
			case 'PerDiemRate':
				return 'Per Diem Rate';
			case 'LoadPayRate':
				return 'Load Pay Rate';
			case 'InstructFlatRate':
				return 'Instructor Flat Rate';
			case 'ApBonusPercentage':
				return 'A&P Bonus Percentage';
			case 'ExportToWorkday':
				return 'Export to Workday';
			case 'IsAvailability':
				return 'Availability';
			case 'IsInstructionGiven':
				return 'Instruction Given';
			case 'isFlatRateInstruction':
				return 'Flat Rate Instruction';
			case 'IsIncentive':
				return 'Incentive';
			case 'IsApHours':
				return 'A&P Hours';
			case 'FlatHourlyRate':
				return 'Flat Hourly Rate';
			case 'IsFlightTime':
				return 'Flight Time';
			case 'LastModifiedByUserId':
				return 'Last Modified By UserId';
			case 'Unit':
				return 'Unit';
			case 'WorkdayPayCode':
				return 'Workday Pay Code';
			case 'FactorAmount':
				return 'Factor Percentage';
			case 'IconName':
				return 'Icon';
			case 'ColorName':
				return 'Icon Color';
			case 'Name':
				return 'Category Name';
			case 'NoteName':
				return 'Note Name';
			case 'FieldType':
				return 'Field Text Type';
			case 'ShortCode':
				return 'Short Code';
			case 'IsRequired':
				return '';
			case 'PerTripPay':
				return 'Trip Pay';
			case 'AvailabilityPay':
				return 'Availability Pay';
			case 'IsInstructorPayScale':
				return 'Show instructor columns';
			case 'IsApRatingPayScale':
				return 'Use global A&P bonus';
			case 'DisplayGuaranteedHours':
				return 'Use hourly rate without guaranteed hours';
			case 'ConstantAPBonus':
				return 'Constant A&P Bonus';
			case 'InstructorPayDollars':
				return 'Instructor Pay';
			case 'YearsOfService':
				return 'Years Of Service';
			case 'GuaranteedHours':
				return 'Guaranteed Hours';
			case 'HourlyRateDollars':
				return 'Hourly Rate';
			case 'NewNoteCreated':
				return 'New Note Created';
			case 'PhoneStipendAmount':
				return 'Phone Stipend Amount';
			case 'IsVisibleToLoadSpecialists':
				return 'Visible to Load Specialists';
			case 'PayCategoryArchived':
				return 'Pay Category archived';
			case 'PayCategoryNoteArchived':
				return `Pay Category Note "${detail.oldValue}" archived`;
			case 'TransactionDate':
				return 'Date';
			case 'PayCategory':
				return 'Category';
			case 'Value':
				return 'Value';
			case 'Comments':
				return 'Comment';
			case 'Note test':
				return 'Note Test';
			case 'Route':
				return 'Route';
			case 'TripStart':
				return 'Block Out';
			case 'TripEnd':
				return 'Block In';
			case 'TripNumber':
				return 'Trip Number';
			case 'BlockTime':
				return 'Block Time';
			case 'RouteInformation':
				return 'Route';
			case 'LoadsUnloadsCount':
				return 'Loads/Unloads Count';
			case 'BrokenTripCount':
				return 'Broken Trip Count';
			case 'IsBypassed':
				return 'Bypassed';
			case 'IsCanceled':
				return 'Canceled';
			case 'Broken':
				return 'Broken';
			case 'Standard':
				return 'Standard';

			default:
				console.error(detail.field);

				return 'UNKNOWN FIELD NAME';
		}
	}

	private isFieldAColorField(field: string): boolean {
		switch (field) {
			case 'Icon Color':
				return true;
			default:
				return false;
		}
	}

	private isThisAFutureChange(effectiveDate: string): boolean {
		const newDate: string = formatDate(effectiveDate, 'yyyy-MM', 'en-US');
		const todayDate: string = formatDate(new Date(), 'yyyy-MM', 'en-US');

		return newDate > todayDate;
	}

	private isFieldAnIconField(field: string): boolean {
		switch (field) {
			case 'Icon':
				return true;
			default:
				return false;
		}
	}
}

export interface IChangedFieldDetailFormatted extends IChangedFieldDetail {
	isColor: boolean;
	isFuture: boolean;
	isIcon: boolean;
}
